'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '../../atoms/button/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../dialog/dialog';

export interface ConfirmationDialogProps {
	/** Se true, il dialog è visibile. */
	open: boolean;
	/** Chiamata quando il dialog deve aprirsi/chiudersi (es. click su Annulla, tasto Esc). */
	onOpenChange: (open: boolean) => void;
	/** Titolo della conferma richiesta. */
	title: string;
	/** Testo che spiega cosa sta per succedere. */
	description: string;
	/** Chiamata alla conferma; se ritorna una Promise, il dialog aspetta il completamento prima di chiudersi e mostra `isLoading`. */
	onConfirm: () => Promise<void> | void;
	/** Se true, disabilita i bottoni e mostra lo spinner sul bottone di conferma. */
	isLoading?: boolean;
	/** `destructive` (default) usa i colori di pericolo per l'azione irreversibile; `default` usa lo stile neutro. */
	variant?: 'default' | 'destructive';
	/** Testo del bottone di conferma. Default: "Conferma". */
	confirmText?: string;
	/** Testo del bottone di annullamento. Default: "Annulla". */
	cancelText?: string;
}

/** Dialog di conferma generico per azioni potenzialmente distruttive o irreversibili (es. eliminazione, revoca). */
export function ConfirmationDialog({
	open,
	onOpenChange,
	title,
	description,
	onConfirm,
	isLoading = false,
	variant = 'destructive',
	confirmText = 'Conferma',
	cancelText = 'Annulla',
}: ConfirmationDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md border-border bg-card">
				<DialogHeader>
					<div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger/10 text-danger">
						<AlertTriangle className="h-5 w-5" />
					</div>
					<DialogTitle className="text-lg font-bold">{title}</DialogTitle>
					<DialogDescription className="text-sm text-text-muted">{description}</DialogDescription>
				</DialogHeader>
				<DialogFooter className="mt-4 flex gap-3">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
						className="flex-1 border-border text-[13px] hover:bg-accent/5"
					>
						{cancelText}
					</Button>
					<Button
						variant={variant === 'destructive' ? 'destructive' : 'default'}
						onClick={async () => {
							await onConfirm();
							onOpenChange(false);
						}}
						disabled={isLoading}
						className={`flex-1 text-[13px] ${variant === 'destructive' ? 'bg-danger hover:bg-danger/90' : ''}`}
					>
						{isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : confirmText}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
